import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// This component receives the OAuth callback from https://auth.emergentagent.com/
// The user lands at `{redirect_url}#session_id=<id>` — we detect the fragment,
// exchange it once for our JWT cookie via POST /api/auth/google/exchange,
// then navigate to /account. See ../context/AuthContext.jsx for the guard that
// skips the initial /auth/me while this callback is running.

export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      setError("Missing Google session — please try signing in again.");
      return;
    }

    (async () => {
      try {
        await api.post("/auth/google/exchange", { session_id: sessionId });
        // Refresh auth context so the app knows we're signed in.
        await refresh();
        toast.success("Signed in with Google");
        // Strip the fragment then send them to their account home.
        window.history.replaceState(null, "", window.location.pathname);
        nav("/account", { replace: true });
      } catch (e) {
        const msg = formatApiError(e.response?.data?.detail) || "Google sign-in failed.";
        setError(msg);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {error ? (
          <>
            <div className="text-sm font-mono uppercase tracking-[0.18em] text-[var(--crimson)]">Sign-in failed</div>
            <p className="mt-3 text-[var(--ink-soft)]" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => nav("/login", { replace: true })}
              className="btn-primary mt-6"
              data-testid="auth-callback-back-to-login"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div className="mx-auto h-10 w-10 rounded-full border-2 border-[var(--terra)] border-t-transparent animate-spin" />
            <p className="mt-4 text-sm text-[var(--ink-soft)]" data-testid="auth-callback-status">Completing Google sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
